import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Car, Users, FileText, Settings, BarChart3, Bell, ChevronLeft, ChevronRight, X, Percent, Shield } from 'lucide-react';
import { ThemeContext } from '../context/ThemeContext';
import ReviewInvitationPopup from './ReviewInvitationPopup';
import { checkReviewEligibility, getFeaturedReviews } from '../services/reviews';
import { isAuthenticated } from '../services/auth';

// Color palette for testimonial avatars (moved outside component for stable reference)
const AVATAR_COLORS = [
  "bg-blue-600", "bg-green-600", "bg-purple-600", "bg-red-600",
  "bg-yellow-600", "bg-indigo-600", "bg-pink-600", "bg-teal-600",
  "bg-orange-600", "bg-cyan-600"
];

// Placeholder testimonials (fallback if no reviews in database)
const PLACEHOLDER_TESTIMONIALS = [
  {
    initials: "JM",
    name: "John Martinez",
    company: "Martinez Auto Repair",
    text: "The vehicle database alone is worth it. I can look up any year, make, and model right from the invoice screen — no more Googling specs. And the free yearly updates keep everything current.",
    rating: 5,
    color: "bg-blue-600"
  },
  {
    initials: "SW",
    name: "Sarah Wilson",
    company: "Wilson Automotive",
    text: "We switched from handwritten invoices and the difference is night and day. Custom line item types for parts, labor, and shop supplies keep our invoices clean and the tax calculates automatically.",
    rating: 5,
    color: "bg-green-600"
  },
  {
    initials: "MJ",
    name: "Michael Johnson",
    company: "Johnson's Garage",
    text: "The reports page is a lifesaver at tax time. I can filter all my invoices by date range and print the whole batch as PDFs. Used to take me a full weekend, now it takes 20 minutes.",
    rating: 5,
    color: "bg-red-600"
  },
  {
    initials: "LC",
    name: "Linda Chen",
    company: "Chen Auto Service",
    text: "Being able to search customers by name and have their info auto-fill on the invoice saves us so much time during check-in. Our front desk loves it.",
    rating: 5,
    color: "bg-yellow-600"
  },
  {
    initials: "DT",
    name: "David Thompson",
    company: "Thompson's Transmission",
    text: "The discount feature is great for repeat customers. I set up a 10% loyalty discount template and my guys can apply it in two clicks. Flat or percentage, per item or whole invoice.",
    rating: 5,
    color: "bg-indigo-600"
  },
  {
    initials: "KP",
    name: "Karen Patterson",
    company: "Patterson Auto Works",
    text: "I like that I can set different tax rates for different item types. Parts are taxed, labor isn't in our state, and the software handles it all automatically on every invoice.",
    rating: 5,
    color: "bg-pink-600"
  },
  {
    initials: "TA",
    name: "Tom Anderson",
    company: "Anderson's Auto Body",
    text: "Setup took about 10 minutes — company name, logo, tax rate, and we were printing invoices. The first-time setup wizard walked us through everything.",
    rating: 5,
    color: "bg-teal-600"
  },
  {
    initials: "NR",
    name: "Nicole Rodriguez",
    company: "Rodriguez Motors",
    text: "We run insurance jobs daily and the claim number field on every invoice keeps things organized. Then I can search by claim number in reports when I need to pull something up.",
    rating: 5,
    color: "bg-orange-600"
  },
  {
    initials: "BW",
    name: "Brian White",
    company: "White's Automotive Center",
    text: "The CSV backup feature gives me peace of mind. I export everything once a week and know all my customer and invoice data is safe. Import works great too if you ever need to restore.",
    rating: 5,
    color: "bg-cyan-600"
  },
  {
    initials: "RG",
    name: "Rachel Green",
    company: "Green's Auto Care",
    text: "Finally, invoicing software that gets auto shops. The service writer and tech fields, mileage tracking, VIN entry — it's all stuff we actually need instead of generic business features.",
    rating: 5,
    color: "bg-purple-600"
  }
];


const HomePage = () => {
  const { darkMode } = useContext(ThemeContext);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [hasNotified, setHasNotified] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [testimonials, setTestimonials] = useState([]);

  // Scroll-triggered fade-in animation for sections
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-10');
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll('.scroll-reveal');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  // Check if user just registered and needs to see verification modal
  useEffect(() => {
    const shouldShowModal = sessionStorage.getItem('showVerificationModal');
    if (shouldShowModal === 'true') {
      setShowVerificationModal(true);
      sessionStorage.removeItem('showVerificationModal');
    }
  }, []);

  // Check if user is eligible for review popup
  useEffect(() => {
    const checkReviewPopup = async () => {
      if (isAuthenticated()) {
        try {
          const eligibility = await checkReviewEligibility();
          if (eligibility.showPopup) {
            setShowReviewPopup(true);
          }
        } catch (err) {
          console.error('Error checking review popup:', err);
        }
      }
    };
    checkReviewPopup();
  }, []);

  // Fetch featured reviews from the database
  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const reviews = await getFeaturedReviews(12);
        if (reviews && reviews.length > 0) {
          // Map API response to testimonial format
          const mappedTestimonials = reviews.map((review, index) => {
            const firstName = review.authorFirstName || 'Anonymous';
            const lastName = review.authorLastName || '';
            const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

            return {
              initials,
              name: `${firstName} ${lastName}`.trim(),
              company: review.authorCompanyName || '',
              text: review.content || '',
              rating: review.rating || 5,
              color: AVATAR_COLORS[index % AVATAR_COLORS.length]
            };
          });
          setTestimonials(mappedTestimonials);
        } else {
          setTestimonials(PLACEHOLDER_TESTIMONIALS);
        }
      } catch (err) {
        console.error('Error fetching testimonials:', err);
        setTestimonials(PLACEHOLDER_TESTIMONIALS);
      }
    };
    fetchTestimonials();
  }, []);

  // Auto-scroll testimonials (scroll by 1 at a time, show multiple based on screen size)
  const [testimonialsToShow, setTestimonialsToShow] = useState(3);
  const testimonialIntervalRef = useRef(null);

  useEffect(() => {
    const updateTestimonialsToShow = () => {
      const width = window.innerWidth;
      if (width >= 1280) setTestimonialsToShow(3); // xl screens
      else if (width >= 768) setTestimonialsToShow(2); // md screens
      else setTestimonialsToShow(1); // small screens
    };

    updateTestimonialsToShow();
    window.addEventListener('resize', updateTestimonialsToShow);
    return () => window.removeEventListener('resize', updateTestimonialsToShow);
  }, []);

  const maxTestimonialIndex = testimonials.length - testimonialsToShow;

  // Function to start/restart the auto-scroll timer
  const startAutoScroll = useCallback(() => {
    // Clear any existing interval
    if (testimonialIntervalRef.current) {
      clearInterval(testimonialIntervalRef.current);
    }
    // Start new interval
    testimonialIntervalRef.current = setInterval(() => {
      setCurrentTestimonial((prev) => {
        if (prev >= maxTestimonialIndex) return 0;
        return prev + 1;
      });
    }, 5000); // Change testimonial every 5 seconds
  }, [maxTestimonialIndex]);

  useEffect(() => {
    startAutoScroll();
    return () => {
      if (testimonialIntervalRef.current) {
        clearInterval(testimonialIntervalRef.current);
      }
    };
  }, [startAutoScroll]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => {
      if (prev >= maxTestimonialIndex) return 0;
      return prev + 1;
    });
    startAutoScroll(); // Reset timer on manual navigation
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => {
      if (prev <= 0) return maxTestimonialIndex;
      return prev - 1;
    });
    startAutoScroll(); // Reset timer on manual navigation
  };

  const goToTestimonial = (index) => {
    setCurrentTestimonial(Math.min(index, maxTestimonialIndex));
    startAutoScroll(); // Reset timer on manual navigation
  };
  
  return (
    <div className={`flex-1 flex flex-col ${darkMode ? 'dark' : ''} bg-white dark:bg-gray-900`}>
      {/* Main content */}
      <main className="flex-grow">
        {/* ============================================ */}
        {/* HERO — Title + CTA                          */}
        {/* ============================================ */}
        <section id="home" className="relative bg-white dark:bg-gray-900 text-center py-20 lg:py-28">
          <div className="px-4 w-full max-w-4xl mx-auto">
            <p className="text-sm md:text-base tracking-widest uppercase text-blue-600 dark:text-blue-400 font-semibold mb-4">Stonewall Invoicing</p>
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-6 leading-[1.05] tracking-tight text-gray-900 dark:text-white">
              Run Your Shop.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 dark:from-blue-400 dark:to-cyan-300">
                We Handle the Rest.
              </span>
            </h1>
            <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
              Create professional invoices, manage customers, and look up any vehicle — all from one app built for auto repair shops.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <Link
                to={isAuthenticated() ? "/account/subscription" : "/register"}
                className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-200"
              >
                {isAuthenticated() ? "My Subscription" : "Start Free Trial"}
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline text-lg font-normal"
              >
                View pricing &rsaquo;
              </Link>
            </div>
            <p className="text-sm text-gray-400 dark:text-gray-500">No credit card required &middot; Free 7-day trial &middot; Cancel anytime</p>
          </div>
        </section>

        {/* ============================================ */}
        {/* Feature Statement                            */}
        {/* ============================================ */}
        <section className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md py-24 lg:py-32">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <h2 className="scroll-reveal opacity-0 translate-y-10 transition-all duration-700 text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white leading-[1.12] tracking-tight mb-6">
              Invoicing, customers, vehicles, and reports.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 dark:from-blue-400 dark:to-cyan-300">
                One app. Zero paperwork.
              </span>
            </h2>
          </div>
        </section>

        {/* ============================================ */}
        {/* Feature Grid — based on actual app features  */}
        {/* ============================================ */}
        <section className="bg-white dark:bg-gray-900 py-24 lg:py-32">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { Icon: FileText, color: 'text-blue-500', title: 'Invoice Creation', desc: 'Create professional invoices with custom line items, automatic tax calculation per item type, auto-incrementing estimate numbers, and support for insurance claim numbers. Print directly or export to PDF.' },
                { Icon: Users, color: 'text-green-500', title: 'Customer Database', desc: 'Store unlimited customer records including name, phone, email, and address. Search by name, auto-populate invoices, and view a customer\'s full invoice history from the Reports screen.' },
                { Icon: Car, color: 'text-purple-500', title: 'Vehicle Database', desc: 'Look up any vehicle by year, make, and model with detailed specs like engine displacement, cylinders, drive type, and fuel type. Manual VIN entry supported. Includes FREE yearly database updates.' },
                { Icon: BarChart3, color: 'text-orange-500', title: 'Reports & Filtering', desc: 'Filter your invoice history by date range, service tech, customer, or claim number. Use quick filters for Today, This Week, or This Month. Print individual invoices or export filtered results to PDF.' },
                { Icon: Percent, color: 'text-red-500', title: 'Flexible Discounts', desc: 'Apply flat dollar or percentage discounts per line item or across the entire invoice. Create reusable discount templates for fast access, and protect against discounts that reduce totals below zero.' },
                { Icon: Settings, color: 'text-teal-500', title: 'Customizable Settings', desc: 'Set custom tax rates per line item type, add your company logo and contact info, write your own invoice disclaimer, create custom item types, and configure printer settings to match your workflow.' },
              ].map(({ Icon, color, title, desc }) => (
                <div key={title} className="scroll-reveal opacity-0 translate-y-10 transition-all duration-700 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className={`w-6 h-6 ${color} flex-shrink-0`} />
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h3>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">{desc}</p>
                </div>
              ))}

              {/* Data Security — full width */}
              <div className="scroll-reveal opacity-0 translate-y-10 transition-all duration-700 md:col-span-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md rounded-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Shield className="w-6 h-6 text-emerald-500 flex-shrink-0" />
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Data Security</h3>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Your data is encrypted and stored only on your computer — no cloud, no third parties, no one else has access. Back up everything with a single click and restore it just as easily if you ever need to.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================ */}
        {/* CTA Statement                                */}
        {/* ============================================ */}
        <section className="scroll-reveal opacity-0 translate-y-10 transition-all duration-700 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md py-24 lg:py-32">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white leading-[1.12] tracking-tight mb-6">
              Less time on paperwork.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 dark:from-blue-400 dark:to-cyan-300">
                More time under the hood.
              </span>
            </h2>
            <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
              Join 100+ auto repair shops running their business with Stonewall Invoicing.
            </p>
            <Link
              to={isAuthenticated() ? "/account/subscription" : "/register"}
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-200"
            >
              {isAuthenticated() ? "Manage subscription" : "Try it free for 7 days"}
            </Link>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="scroll-reveal opacity-0 translate-y-10 transition-all duration-700 bg-gray-50 dark:bg-gray-900 py-20">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-5 lg:px-7">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Trusted by Auto Repair Professionals
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                Hear what our customers have to say about how Stonewall Invoicing has transformed their business operations.
              </p>
            </div>

            <div className="relative max-w-7xl mx-auto">
              <div className="flex items-center gap-4">
                {/* Left Navigation Arrow */}
                <button
                  onClick={prevTestimonial}
                  className="flex-shrink-0 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-white p-3 rounded-full shadow-lg transition-colors z-10"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>

                {/* Testimonial Carousel Container */}
                <div className="flex-grow overflow-hidden">
                  <div
                    className="flex transition-transform duration-700 ease-in-out"
                    style={{
                      transform: `translateX(-${currentTestimonial * (100 / testimonialsToShow)}%)`,
                    }}
                  >
                    {testimonials.map((testimonial, index) => (
                      <div
                        key={index}
                        className="flex-shrink-0 px-4"
                        style={{ width: `${100 / testimonialsToShow}%` }}
                      >
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-600 p-6 h-full">
                          <div className="flex items-center mb-4">
                            <div className={`${testimonial.color} rounded-full w-12 h-12 flex items-center justify-center mr-4`}>
                              <span className="text-white font-bold">{testimonial.initials}</span>
                            </div>
                            <div>
                              <h4 className="font-bold text-gray-900 dark:text-white">{testimonial.name}</h4>
                              <p className="text-gray-600 dark:text-gray-400 text-sm">{testimonial.company}</p>
                            </div>
                          </div>
                          <p className="text-gray-700 dark:text-gray-300 italic mb-4">
                            "{testimonial.text}"
                          </p>
                          <div className="flex text-yellow-400">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 0 0 .95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 0 0 -.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 0 0 -1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 0 0 -.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 0 0 .951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                            {[...Array(5 - testimonial.rating)].map((_, i) => (
                              <svg key={`empty-${i}`} className="w-5 h-5 text-gray-300 dark:text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 0 0 .95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 0 0 -.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 0 0 -1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 0 0 -.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 0 0 .951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right Navigation Arrow */}
                <button
                  onClick={nextTestimonial}
                  className="flex-shrink-0 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-800 dark:text-white p-3 rounded-full shadow-lg transition-colors z-10"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>

              {/* Dots Indicator - Show all 10 reviews, highlight based on adaptive count */}
              <div className="flex justify-center mt-8 space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToTestimonial(index)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      index >= currentTestimonial && index < currentTestimonial + testimonialsToShow
                        ? 'bg-blue-600 dark:bg-blue-400'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="scroll-reveal opacity-0 translate-y-10 transition-all duration-700 py-20 bg-white dark:bg-gray-900">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-5 lg:px-7">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Simple, Transparent Pricing
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                No tiers, no feature gates. Every subscription gets full access to every feature.
              </p>
            </div>

            {/* Stonewall Invoicing card */}
            <div className="max-w-5xl mx-auto mb-8">
              <div className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md rounded-lg p-6 md:p-10">
                <div className="grid md:grid-cols-2 gap-10">

                  {/* Left — Plans & CTA */}
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Stonewall Invoicing</h3>
                      <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2.5 py-1 rounded-lg font-semibold">Save up to 30%</span>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">Full access to every feature. Pick your billing cycle.</p>

                    <div className="space-y-2 mb-6">
                      <div
                        onClick={() => window.location.href = isAuthenticated() ? "/account/subscription" : "/register"}
                        className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 cursor-pointer hover:border-gray-300 dark:hover:border-gray-500 transition-colors"
                      >
                        <span className="font-medium text-gray-900 dark:text-white">Monthly</span>
                        <span className="font-bold text-gray-900 dark:text-white">$119.99<span className="text-sm font-normal text-gray-400">/mo</span></span>
                      </div>
                      <div
                        onClick={() => window.location.href = isAuthenticated() ? "/account/subscription" : "/register"}
                        className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 cursor-pointer hover:border-gray-300 dark:hover:border-gray-500 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white">Quarterly</span>
                          <span className="text-[11px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-lg font-semibold">Save $29.98</span>
                        </div>
                        <span className="font-bold text-gray-900 dark:text-white">$109.99<span className="text-sm font-normal text-gray-400">/mo</span></span>
                      </div>
                      <div
                        onClick={() => window.location.href = isAuthenticated() ? "/account/subscription" : "/register"}
                        className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 cursor-pointer hover:border-gray-300 dark:hover:border-gray-500 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white">Biannual</span>
                          <span className="text-[11px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-lg font-semibold">Save $119.95</span>
                        </div>
                        <span className="font-bold text-gray-900 dark:text-white">$99.99<span className="text-sm font-normal text-gray-400">/mo</span></span>
                      </div>
                      <div
                        onClick={() => window.location.href = isAuthenticated() ? "/account/subscription" : "/register"}
                        className="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-gray-700 border-2 border-blue-500 dark:border-blue-400 cursor-pointer hover:border-blue-600 dark:hover:border-blue-300 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white">Annual</span>
                          <span className="text-[11px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-lg font-semibold">Save $439.89</span>
                          <span className="text-[10px] bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded-lg font-bold uppercase tracking-wide">Best value</span>
                        </div>
                        <span className="font-bold text-gray-900 dark:text-white">$999.99<span className="text-sm font-normal text-gray-400">/yr</span></span>
                      </div>
                    </div>

                    <Link
                      to={isAuthenticated() ? "/account/subscription" : "/register"}
                      className="w-full inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-lg text-base font-semibold transition-colors"
                    >
                      {isAuthenticated() ? 'Manage Subscription' : 'Get Started'}
                    </Link>
                    <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-2">7-day free trial &middot; No credit card required</p>
                  </div>

                  {/* Right — What's included */}
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-5">What's included</h3>
                    <ul className="space-y-3">
                      {[
                        'Unlimited invoices',
                        'Customer database with search',
                        'Complete vehicle database with FREE yearly updates',
                        'Reports with advanced filtering',
                        'Flexible discounts (per-item and invoice-level)',
                        'PDF generation and direct printing',
                        'Custom line item types and tax rates',
                        'Company branding and logo',
                        'Encrypted local database',
                        'Automatic app updates',
                      ].map((feature) => (
                        <li key={feature} className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                </div>
              </div>
            </div>

            {/* Cloud — Coming Soon */}
            <div className="max-w-5xl mx-auto">
              <div className="bg-gray-900 dark:bg-[#111] rounded-lg p-8 md:p-12 text-white">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                  <div className="md:max-w-xl">
                    <span className="text-xs uppercase tracking-widest text-blue-400 font-semibold">Coming Soon</span>
                    <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-3 tracking-tight">Stonewall Cloud</h2>
                    <p className="text-gray-400 mb-6">
                      Access your business data from anywhere. Multi-user support, real-time sync, automatic cloud backups, and cross-device compatibility.
                    </p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {[
                        'All Professional features',
                        'Unlimited users',
                        'Cloud data sync',
                        'Real-time collaboration',
                        'Cross-device access',
                        'Automatic backups',
                      ].map((item) => (
                        <div key={item} className="flex items-center">
                          <svg className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-300">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      className={`px-8 py-3.5 rounded-lg text-base font-semibold transition-colors ${
                        hasNotified
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-white text-gray-900 hover:bg-gray-100'
                      }`}
                      onClick={() => setShowNotifyModal(true)}
                    >
                      {hasNotified ? 'Notified!' : 'Notify Me'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* CTA Section */}
        <section className="scroll-reveal opacity-0 translate-y-10 transition-all duration-700 bg-blue-600 dark:bg-blue-800 py-16 text-white">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-5 lg:px-7 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Automotive Business?</h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Join 100+ auto repair shops that trust Stonewall Invoicing to streamline their operations.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link
                to={isAuthenticated() ? "/account/subscription" : "/register"}
                className="bg-white text-blue-600 hover:bg-blue-200 px-6 py-3 rounded-lg text-lg font-medium inline-block"
              >
                {isAuthenticated() ? "My Subscription" : "Start Free Trial"}
              </Link>
              <Link
                to="/pricing"
                className="bg-transparent border border-white text-white hover:bg-blue-500 px-6 py-3 rounded-lg text-lg font-medium inline-block"
              >
                View Pricing
              </Link>
              <Link
                to="/about"
                className="bg-transparent border border-white text-white hover:bg-blue-500 px-6 py-3 rounded-lg text-lg font-medium inline-block"
              >
                About Us
              </Link>
              <Link
                to="/contact"
                className="bg-transparent border border-white text-white hover:bg-blue-500 px-6 py-3 rounded-lg text-lg font-medium inline-block"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Email Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 flex items-center justify-center p-4 pt-[52px]">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-8 relative transform transition-all">
            <button
              onClick={() => setShowVerificationModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Verify Your Email Address
              </h3>

              <p className="text-gray-600 dark:text-gray-300 mb-6 text-lg">
                We've sent a verification email to your inbox. <strong>Please verify your email to receive your trial license key</strong> and start using Stonewall Invoicing.
              </p>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <strong>Note:</strong> If you don't see the email, please check your spam or junk folder.
                </p>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-6">
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  <strong>Important:</strong> You won't be able to download the software until you verify your email address.
                </p>
              </div>

              <button
                onClick={() => setShowVerificationModal(false)}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg"
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notify Me Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 relative transform transition-all">
            <button
              onClick={() => setShowNotifyModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Get Notified About Stonewall Cloud</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Be the first to know when our cloud solution launches. Enter your email below and we'll keep you updated!
              </p>
            </div>
            
            {!hasNotified ? (
              <form onSubmit={(e) => {
                e.preventDefault();
                setHasNotified(true);
              }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={notifyEmail}
                  onChange={(e) => setNotifyEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  placeholder="your@email.com"
                  required
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowNotifyModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Notify Me
                </button>
              </div>
            </form>
            ) : (
              <div className="text-center">
                <div className="mb-6">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Thank You!</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    We've added {notifyEmail} to our notification list. You'll be the first to know when Stonewall Cloud launches!
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowNotifyModal(false);
                  }}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Review Invitation Popup */}
      {showReviewPopup && (
        <ReviewInvitationPopup onClose={() => setShowReviewPopup(false)} />
      )}
    </div>
  );
};

export default HomePage;